// frontend/src/pages/membership/planRelation.ts — P3 Upgrade/Downgrade
//
// Berechnet die Beziehung der gewählten Paywall-Karte (tier+duration) zur
// aktiven Mitgliedschaft des Nutzers. Die Paywall braucht diese Information,
// um CTA-Label, -Verhalten und Hinweistexte richtig zu setzen:
//   - 'none'      → keine aktive Mitgliedschaft ODER Fackelträger (Trial zählt)
//   - 'current'   → exakt gleiche Stufe + gleiche Laufzeit → „Dein Tarif"
//   - 'upgrade'   → höhere Stufe ODER gleiche Stufe mit LÄNGERER Laufzeit
//   - 'downgrade' → niedrigere Stufe ODER gleiche Stufe mit KÜRZERER Laufzeit
//
// Stufen-Rang (höher = besser): plus < ultra < founders.
// Laufzeit-Rang (länger = besser): week < month < year.
// Trial-Status zählt wie aktiv (User hat aktuell Zugang zur Stufe).
// torchbearer → IMMER 'none' (Segment-Vorgabe §1, P1).
// duration 'custom' → IMMER 'none' (Paket 75): Admin-Vergaben, Bestandsschutz
// und Geschenke sind keine Store-Abos. Ein Kauf daneben ist ein NEUER Kauf zum
// vollen Preis — kein Wechsel mit Ersatz-Token (Android warf sonst
// replace_token_missing, z. B. Ultra-Tester → Founders).
// Bezugspunkt ist deshalb das aktive STORE-Abo (relationMembership), nicht die
// höchste aktive Mitgliedschaft: Ein Geschenk verdeckt dort sonst ein
// bezahltes Abo, und ein Wechsel würde ohne Ersatz-Token zum Parallelkauf.

import type { ActiveMembership, MembershipDuration, MembershipSnapshot } from '../../api/membership';

export type PaywallTier = 'plus' | 'ultra' | 'founders';
export type PaywallDuration = Exclude<MembershipDuration, 'custom'>;
export type PlanRelation = 'none' | 'current' | 'upgrade' | 'downgrade';

const TIER_RANK: Record<PaywallTier, number> = {
  plus: 1,
  ultra: 2,
  founders: 3,
};

const DURATION_RANK: Record<PaywallDuration, number> = {
  week: 1,
  month: 2,
  year: 3,
};

// Gekündigte Store-Verträge bleiben bis zum bestätigten Periodenende Wechselbasis.
const ACTIVE_RELATION_STATUSES = new Set(['trial', 'active', 'grace', 'cancelled']);

export function isMembershipActiveForRelation(membership: ActiveMembership | null): boolean {
  if (!membership) return false;
  if (membership.tier === 'torchbearer' || membership.duration === 'custom' || !isStoreProvider(membership.provider)) return false;
  const end = Date.parse(membership.current_period_end ?? '');
  if (!Number.isFinite(end) || end <= Date.now()) return false;
  return ACTIVE_RELATION_STATUSES.has(String(membership.status || '').toLowerCase());
}

// Paket 75 (Review-Befund aus 75-kauf und 75-geschenke): Mitgliedschaft,
// gegen die die Paywall rechnet — Relation, Ersatz-Token, Trial-Wunsch,
// Laufzeitende und Store-Konflikt (isCrossStoreMembership).
// Beispiel: bezahltes Google-Plus + 7 Tage Ultra geschenkt (oder Admin-Ultra)
// → `membership` ist das Geschenk (custom), `storeMembership` das Plus-Abo.
// Ultra/Founders sind dann ein Wechsel ('upgrade', Ersatz-Token des
// Plus-Abos), kein zweiter Kauf. null = kein Store-Abo (jeder Kauf ist ein
// Neuabschluss). Liefert der Server kein `store_membership` (undefined,
// älterer Server), gilt wie bisher die oberste Mitgliedschaft.
export function relationMembership(
  snapshot: Pick<MembershipSnapshot, 'membership' | 'storeMembership'>,
): ActiveMembership | null {
  return snapshot.storeMembership !== undefined ? snapshot.storeMembership : snapshot.membership;
}

export function getPlanRelation(
  current: ActiveMembership | null,
  tier: PaywallTier,
  duration: PaywallDuration,
  platform?: { isNative: boolean; os: string },
): PlanRelation {
  if (!current || current.tier === 'torchbearer') return 'none';
  // Paket 75 (Review-Befund aus 75-kauf und 75-geschenke): Nicht-Store-
  // Mitgliedschaften (Admin-Vergabe, Bestandsschutz, Geschenk — immer
  // duration 'custom') haben KEINE Store-Gegenseite, die sich per Replace-API
  // wechseln oder zum Laufzeitende umstellen ließe. Vorher lieferten sie
  // 'upgrade'/'downgrade' wie ein echtes Store-Abo, obwohl dort nie ersetzt
  // werden kann (kein oldPurchaseToken) — die Paywall versprach also einen
  // Wechsel/eine Verrechnung, die real ein sofortiger Neukauf zum vollen Preis
  // war. Ein Kauf ist hier daher IMMER ein normaler Neuabschluss ('none').
  if (current.duration === 'custom') return 'none';
  if (!isMembershipActiveForRelation(current)) return 'none';
  if (platform && currentStoreChannel(platform) && current.provider !== currentStoreChannel(platform)
    && (current.auto_renew_enabled === false || current.status === 'cancelled' || !!current.usable_until)) return 'none';

  // current.tier kann 'free' sein (sollte hier nie passieren, aber defensive):
  // in dem Fall ebenfalls 'none', damit ein Downgrade nicht fälschlich
  // angezeigt wird.
  const currentTier = current.tier as PaywallTier | 'free' | 'torchbearer';
  if (currentTier === 'free' || currentTier === 'torchbearer') return 'none';

  // Gleiche Stufe und gleiche Laufzeit → aktueller Tarif.
  if (currentTier === tier && current.duration === duration) return 'current';

  // Stufen-Vergleich (Stufe > Stufe = upgrade, sonst downgrade).
  const currentTierRank = TIER_RANK[currentTier] ?? 0;
  const targetTierRank = TIER_RANK[tier] ?? 0;

  if (targetTierRank > currentTierRank) return 'upgrade';
  if (targetTierRank < currentTierRank) return 'downgrade';

  // Gleiche Stufe → Laufzeit-Vergleich (längere Laufzeit = upgrade,
  // weil der User mehr im Voraus zahlt und mehr Restlaufzeit bekommt).
  const currentDuration = current.duration as PaywallDuration;
  const currentDurationRank = DURATION_RANK[currentDuration] ?? 0;
  const targetDurationRank = DURATION_RANK[duration] ?? 0;

  if (targetDurationRank > currentDurationRank) return 'upgrade';
  if (targetDurationRank < currentDurationRank) return 'downgrade';
  return 'current';
}

// Paket 75-kauf/3: Wechsel über Store-Grenzen.
//
// `memberships.provider` kennt neben den beiden echten Stores auch
// Nicht-Store-Werte (Admin-Grant, Bestandsschutz-Rabattkonto, künftig auch
// Geschenk-Mitgliedschaften o. Ä.) — dafür gibt es keine Store-Gegenseite,
// die sich per Replace-API "wechseln" ließe. Ein Kauf ist dort immer ein
// normaler Neuabschluss, kein Wechsel.
export type StoreProvider = 'apple' | 'google';

export function isStoreProvider(provider: string | null | undefined): provider is StoreProvider {
  return provider === 'apple' || provider === 'google';
}

// Store-Kanal des aktuellen Geräts — null, wenn auf diesem Gerät ohnehin kein
// In-App-Kauf möglich ist (Web; dort blendet ctaDisabled/app_only bereits
// jeden Kauf-Button aus).
export function currentStoreChannel(platform: { isNative: boolean; os: string }): StoreProvider | null {
  if (!platform.isNative) return null;
  if (platform.os === 'ios') return 'apple';
  if (platform.os === 'android') return 'google';
  return null;
}

// Ist die aktive Mitgliedschaft über einen ANDEREN Store abgeschlossen als
// das aktuelle Gerät (Apple-Abo auf Android, Google-Abo auf iOS, Web-Abo in
// der App)? Dann kann kein Store-Wechsel per Replace-API funktionieren —
// weder Apple noch Google kennen den Kauf des jeweils anderen Stores
// (Symptom: `replace_token_missing`, weil sich auf DIESEM Gerät nie ein
// passender oldPurchaseToken finden lässt). Admin-/Bestandsschutz-
// Mitgliedschaften zählen NICHT als Konflikt — dafür ist immer ein normaler
// Neuabschluss möglich (siehe isStoreProvider).
export function isCrossStoreMembership(
  current: ActiveMembership | null,
  platform: { isNative: boolean; os: string },
): boolean {
  if (!current || !isMembershipActiveForRelation(current)) return false;
  if (!isStoreProvider(current.provider)) return false;
  // Review-Befund 75-kauf (Minor): Ein im anderen Store bereits gekündigtes
  // Abo (auto_renew_enabled === false, laeuft nur noch bis current_period_end
  // aus — Status bleibt dabei 'active'/'trial'/'grace', s. membership-store.js
  // DID_CHANGE_RENEWAL_STATUS) ist kein Wechsel-Konflikt mehr: es verlaengert
  // sich nicht von selbst, ein Neukauf auf DIESEM Store bucht also nicht
  // doppelt ab. Statt zu sperren erlauben wir hier einen normalen Neukauf
  // ohne Ersetzen (Hinweis auf das auslaufende Abo zeigt die Paywall separat).
  if (current.auto_renew_enabled === false || current.status === 'cancelled' || !!current.usable_until) return false;
  const channel = currentStoreChannel(platform);
  if (!channel) return false;
  return current.provider !== channel;
}

export function nextUpgradeTier(current: ActiveMembership | null): PaywallTier | null {
  if (!current) return null;
  if (current.tier === 'torchbearer') return null;
  if (!isMembershipActiveForRelation(current)) return null;
  const currentTier = current.tier as PaywallTier;
  if (currentTier === 'plus') return 'ultra';
  if (currentTier === 'ultra') return 'founders';
  return null; // founders hat keine bezahlte Stufe darüber (torchbearer ist Admin/Grant)
}

export const _test_only = {
  TIER_RANK,
  DURATION_RANK,
  ACTIVE_RELATION_STATUSES,
};
